import { useMemo } from "react";
import { COLORS } from "../../theme/colors";
import { Badge, Input, Select, Button } from "../../components/ui";

const CHANNEL_OPTIONS = [
  { value: "chat", label: "Chat" },
  { value: "voice", label: "Voice" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sms", label: "SMS" },
  { value: "webhook", label: "Webhook" },
];

const ESCALATED_OPTIONS = [
  { value: null, label: "Any" },
  { value: true, label: "Escalated" },
  { value: false, label: "Not escalated" },
];

// Phase 1.5 #3 · filter bar for the Supervisor Conversations tab.
// Owned state (q, channels, agentId, escalated) lives in the parent;
// this component is a presentational shell so URL/sessionStorage wiring
// is the parent's responsibility.
export default function ConversationsFilterBar({
  q, onQChange,
  channels = [], onChannelsChange,
  agentId = "", onAgentIdChange,
  escalated = null, onEscalatedChange,
  agentOptions = [],
}) {
  const filterCount = useMemo(() => {
    return [
      q && q.length > 0,
      channels.length > 0,
      Boolean(agentId),
      escalated !== null,
    ].filter(Boolean).length;
  }, [q, channels, agentId, escalated]);

  const toggleChannel = (val) => {
    if (channels.includes(val)) onChannelsChange(channels.filter(c => c !== val));
    else onChannelsChange([...channels, val]);
  };

  const reset = () => {
    onQChange("");
    onChannelsChange([]);
    onAgentIdChange("");
    onEscalatedChange(null);
  };

  const pillStyle = (active) => ({
    padding: "4px 10px",
    borderRadius: 999,
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    background: active ? `${COLORS.accent}20` : "transparent",
    color: active ? COLORS.accent : COLORS.textMuted,
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
    transition: "all 120ms ease",
  });

  return (
    <div
      style={{
        padding: 14,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
      data-testid="conversations-filter-bar"
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input
          type="text"
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search messages (full-text)…"
          aria-label="Search conversation messages"
          data-testid="filter-q"
          style={{ flex: 1 }}
        />
        {filterCount > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={reset}
            data-testid="filter-reset"
            title="Clear all filters"
          >
            Clear ({filterCount})
          </Button>
        )}
      </div>

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Channels</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} data-testid="filter-channels">
            {CHANNEL_OPTIONS.map(opt => {
              const active = channels.includes(opt.value);
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => toggleChannel(opt.value)}
                  style={pillStyle(active)}
                  data-testid={`channel-${opt.value}${active ? "-active" : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Agent</div>
          <Select
            value={agentId}
            onChange={(e) => onAgentIdChange(e.target.value)}
            aria-label="Filter by agent"
            data-testid="filter-agent"
            style={{ minWidth: 180 }}
          >
            <option value="">All agents</option>
            {agentOptions.map(a => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </Select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em" }}>Escalation</div>
          <div style={{ display: "flex", gap: 4 }} data-testid="filter-escalated">
            {ESCALATED_OPTIONS.map(opt => {
              const active = escalated === opt.value;
              return (
                <button
                  type="button"
                  key={String(opt.value)}
                  onClick={() => onEscalatedChange(opt.value)}
                  style={pillStyle(active)}
                  data-testid={`escalated-${String(opt.value)}${active ? "-active" : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {q && q.length > 0 && (
        <div style={{ fontSize: 11, color: COLORS.textMuted }} data-testid="filter-q-hint">
          Searching message bodies for: <strong style={{ color: COLORS.text }}>{q}</strong>
        </div>
      )}
    </div>
  );
}
